/**
 * Maps the raw infra `Agent` (from the WS gateway) into the shell's
 * `DemoAgent` shape so the existing UI (AgentCard, AgentCarousel,
 * AgentProfilePanel) can render live results without changes.
 *
 * UI-only fields (avatarInitials, avatarBg, specialties, reviews,
 * stats) are derived / stubbed because infra doesn't carry them yet.
 */

import type { Agent as LiveAgent } from "./types";
import type { DemoAgent, TaskCategory } from "@/lib/app-types";

const AVATAR_PALETTE = ["#8635F6", "#60A5FA", "#F6AE2D", "#4ADE80"];

function initialsFromName(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "AG";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

function avatarBgFromAddress(addr: string): string {
  // Deterministic pick from a small palette based on the address digits.
  let sum = 0;
  for (let i = 0; i < addr.length; i++) sum = (sum + addr.charCodeAt(i)) % 1024;
  return AVATAR_PALETTE[sum % AVATAR_PALETTE.length];
}

function categoriesToTaskCategories(cats: string[]): TaskCategory[] {
  const mapped = cats
    .map((c) => c.toLowerCase())
    .map((c): TaskCategory | null => {
      if (["marketing", "seo", "growth"].includes(c)) return "marketing";
      if (["writing", "content", "copy"].includes(c)) return "writing";
      if (["data", "analytics", "sql"].includes(c)) return "data";
      if (["code", "review", "dev", "programming"].includes(c)) return "code";
      return null;
    })
    .filter((c): c is TaskCategory => c !== null);
  return mapped.length > 0 ? mapped : ["general"];
}

/** Convert USDC 6-decimal integer to whole dollars. */
function usdcToDollars(n: number): number {
  return Math.round(n / 1_000_000);
}

/** Convert rating basis points (0-5000, where 4500 = 4.5/5) to 0-5. */
function bpsToRating(bps: number): number {
  return Math.round((bps / 1000) * 10) / 10;
}

export function toDemoAgent(a: LiveAgent): DemoAgent {
  const rating = bpsToRating(a.rating);
  const priceMin = usdcToDollars(a.priceRange.min);
  const priceMax = usdcToDollars(a.priceRange.max);
  const basePrice = Math.round((priceMin + priceMax) / 2) || 25;

  return {
    id: a.address || a.peerId,
    name: a.name,
    avatarInitials: initialsFromName(a.name),
    avatarBg: avatarBgFromAddress(a.address || a.peerId),
    rating,
    tasksCompleted: a.totalTasks,
    tags: a.categories.slice(0, 2),
    specialties: a.categories.length > 0 ? a.categories.slice(0, 4) : ["General"],
    basePrice,
    priceRange: `$${priceMin} — $${priceMax}`,
    status: a.online ? "available" : "offline",
    description: a.description || `Agent on the BotWork P2P network.`,
    categories: categoriesToTaskCategories(a.categories),
    stats: {
      success: rating >= 4 ? "98%" : rating >= 3 ? "92%" : "85%",
      avgSpeed: "4m 00s",
      disputes: "0",
    },
    reviews: [],
  };
}
